import { useState } from "react";
import { useAddDistance, useRateSearch, useSetBoqLine, type BoqDetail } from "../../api/boq";
import { formatMoney, formatQuantity } from "../../api/money";
import { useDebounced } from "../../hooks/useDebounced";

interface Props {
  detail: BoqDetail;
}

/** The standalone tool's Schedule of Rates search, against the real rate
 * card: search by payment ref or wording, narrow by schedule, and add a line
 * at the tendered rate -- never at one typed here. Plus the distance line: a
 * route plan's kilometres onto a per-km rate.
 *
 * Folded away under its heading: a bill raised from a quotation arrives with
 * its lines, so adding one is the exception. It opens by itself only on a bill
 * with no lines yet, where it is the only way to start. */
export default function RatePicker({ detail }: Props) {
  const [query, setQuery] = useState("");
  const [schedule, setSchedule] = useState("");
  const [qtyFor, setQtyFor] = useState<Record<number, string>>({});
  // Asked once the typing pauses, not on every keystroke (Fix #18).
  const settled = useDebounced(query, 250);
  const search = useRateSearch(detail.id, settled, schedule);
  const setLine = useSetBoqLine(detail.id);
  const distance = useAddDistance(detail.id);
  const [planId, setPlanId] = useState("");
  const [kmRateId, setKmRateId] = useState("");

  return (
    <details className="rate-picker-fold" open={detail.lines.length === 0}>
      <summary>Add a line from the Schedule of Rates</summary>
      <fieldset className="header-form cm-form rate-picker" aria-label="Add a line from the Schedule of Rates">
        <p className="derived">Search by Payment Ref or wording; the rate comes from the tender, not from here.</p>
        <div className="rate-picker-row">
          <label className="rate-picker-search">
            Search
            <input
              type="search"
              value={query}
              placeholder="crane, B15, over pumping…"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label className="rate-picker-schedule">
            Schedule
            <select value={schedule} onChange={(event) => setSchedule(event.target.value)}>
              <option value="">Any</option>
              {detail.schedules.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {search.isError ? <p className="notice bad">{search.error.message}</p> : null}
        {search.data && search.data.matches.length > 0 ? (
          <ul className="sor-matches">
            {search.data.matches.map((item) => (
              <li key={item.id}>
                <span className="register-ref">{item.payment_ref}</span>
                <span className="sor-desc">{item.description}</span>
                <span className="sor-rate">
                  {item.rate === null ? "unpriced" : `${formatMoney(item.rate)} / ${item.unit ?? ""}`}
                </span>
                {item.on_boq ? (
                  <span className="badge badge-clean">on BOQ</span>
                ) : (
                  <>
                    <input
                      className="num"
                      aria-label={`${item.payment_ref} quantity`}
                      value={qtyFor[item.id] ?? "1"}
                      onChange={(event) => setQtyFor({ ...qtyFor, [item.id]: event.target.value })}
                    />
                    <button
                      type="button"
                      className="row-button"
                      aria-label={`Add ${item.payment_ref}`}
                      disabled={setLine.isPending}
                      onClick={() => setLine.mutate({ rate_item_id: item.id, qty: qtyFor[item.id] ?? "1" })}
                    >
                      Add
                    </button>
                  </>
                )}
              </li>
            ))}
            {search.data.truncated ? (
              <li className="derived">The list was cut short — narrow the search; an item not shown may still exist.</li>
            ) : null}
          </ul>
        ) : search.data && (settled.trim() !== "" || schedule !== "") ? (
          <p className="derived">Nothing on the schedule matches.</p>
        ) : null}
        {setLine.isError ? <p className="notice bad">{setLine.error.message}</p> : null}

        {detail.km_rates.length > 0 && detail.route_plans.length > 0 ? (
          <div className="rate-picker-distance">
            <h4>Distance from a route plan</h4>
            <div className="rate-picker-row">
              <label>
                Route plan
                <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
                  <option value="">Choose…</option>
                  {detail.route_plans.map((plan) => (
                    <option key={plan.id} value={plan.id} disabled={plan.km === null}>
                      {plan.name} {plan.km === null ? "(no days yet)" : `— ${formatQuantity(plan.km)} km`}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Per-km rate
                <select value={kmRateId} onChange={(e) => setKmRateId(e.target.value)}>
                  <option value="">Choose…</option>
                  {detail.km_rates.map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.payment_ref} {rate.description} ({formatMoney(rate.rate)}/km)
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={distance.isPending || planId === "" || kmRateId === ""}
                onClick={() => distance.mutate({ rate_item_id: Number(kmRateId), plan_id: Number(planId) })}
              >
                Add the kilometres
              </button>
            </div>
            {distance.isError ? <p className="notice bad">{distance.error.message}</p> : null}
          </div>
        ) : null}
      </fieldset>
    </details>
  );
}
